import {
  BlueDarkText,
  BlueText,
  BoldText,
  MuteText,
  PinkDarkText,
  SuccessDarkText,
} from '@elements/Common';
import { CommonUtility } from '@utility/common';
import { cn } from '@skupreme/shared/ui';
import React, { type ComponentPropsWithoutRef, Fragment } from 'react';
import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import { Equals, Info, Minus, Plus } from '@phosphor-icons/react';
import { SimpleDarkToolTip } from '@elements/Popup';
import {
  FormulaContainer,
  parseNumber,
  RenderMutedDays,
  StyledPopup,
} from './formulaCommon';
import { tooltTipsText } from './Chart';

type DivProps = ComponentPropsWithoutRef<'div'>;

function ProviderNames({ children }: { children: React.ReactNode }) {
  return (
    <span className="sk:max-w-[200px] sk:whitespace-break-spaces!">
      {children}
    </span>
  );
}

export function FCInventory({
  predictedOrderBuffer,
  other_fp_credit,
  velocityProvider,
  smallTooltips,
}) {
  if (!predictedOrderBuffer) return 'Not Enough Data';

  return (
    <StyledPopup className={smallTooltips ? 'small-height' : ''}>
      <div>
        <span>Primary Warehouse Velocity</span>
        <span>=</span>
        <span>{velocityProvider}</span>
        <MuteText>units / day</MuteText>
      </div>
      <div>
        <span>
          Quantity in Days of{' '}
          {predictedOrderBuffer?.FPCreditDetails?.[0]?.providerName}
        </span>
        <span>=</span>
        <span>
          {
            predictedOrderBuffer?.FPCreditDetails?.[0]
              ?.existingInventoryProvider
          }
          <MuteText>units</MuteText>/
        </span>
        <span>{velocityProvider}</span>
        <span>=</span>
        <span>
          <RenderMutedDays
            days={CommonUtility.roundNumber(
              predictedOrderBuffer?.FPCreditDetails?.[0]
                ?.creditInventoryProvider,
              1,
            )}
          />
        </span>
      </div>
      {predictedOrderBuffer?.FPCreditDetails?.map(FC => (
        <>
          <div className="light-border sk:my-2!" />
          <table cellSpacing="0">
            <tr>
              <td>
                <b>{FC?.providerName}</b>
              </td>
              <td />
              <td />
            </tr>

            <tr>
              <td>
                <BlueDarkText className="sk:m-0! sk:p-0!">
                  Fulfillment Center
                </BlueDarkText>
              </td>
              <td className="sk:mx-5!" colSpan={2}>
                <BlueDarkText className="sk:m-0! sk:p-0!">
                  {FC?.existingInventoryProvider}
                  <span className="sk:pl-1!">units</span> / {velocityProvider}
                  <span className="sk:mx-1!">=</span>
                  <RenderMutedDays
                    renderMuted={false}
                    days={CommonUtility.roundNumber(
                      FC?.creditInventoryProvider,
                      1,
                    )}
                  />
                </BlueDarkText>
              </td>
            </tr>
            {FC?.shipmentDetails?.length > 0 && (
              <tr>
                <td className="sk:pt-3.5!">
                  <b>Shipments</b>
                </td>
                <td />
                <td />
              </tr>
            )}

            {FC?.shipmentDetails?.map(shipment => (
              <tr>
                <td>{shipment.shipmentDisplayId}</td>
                <td className="sk:mx-5!">
                  {shipment.inventoryShipment}
                  <MuteText>units</MuteText>
                </td>
                <td>
                  (
                  <RenderMutedDays
                    days={CommonUtility.roundNumber(
                      shipment.creditInventoryShipment,
                      1,
                    )}
                  />
                  )
                </td>
              </tr>
            ))}
            {FC?.shipmentDetails?.length > 0 && (
              <tr>
                <td className="total">
                  <SuccessDarkText className="sk:m-0! sk:p-0!">
                    Shipment Total
                  </SuccessDarkText>
                </td>
                <td className="sk:mx-5! total">
                  <SuccessDarkText className="sk:m-0! sk:p-0!">
                    {FC?.inventoryShipment}
                    <span className="sk:pl-1!">units</span>
                  </SuccessDarkText>
                </td>
                <td className="total">
                  <SuccessDarkText className="sk:m-0! sk:p-0!">
                    (
                    <RenderMutedDays
                      renderMuted={false}
                      days={CommonUtility.roundNumber(
                        FC?.creditInventoryShipment,
                        1,
                      )}
                    />
                    )
                  </SuccessDarkText>
                </td>
              </tr>
            )}

            <tr>
              <td className="sk:pt-3.5!">
                <b>Variables</b>
              </td>
              <td />
              <td />
            </tr>

            <tr>
              <td>Lead Time</td>
              <td className="sk:mx-5!">
                <RenderMutedDays days={Math.abs(FC?.transitTime)} />
              </td>
              <td />
            </tr>
            <tr>
              <td>Inbound buffer</td>
              <td className="sk:mx-5!">
                <RenderMutedDays days={Math.abs(FC?.inboundBuffer)} />
              </td>
              <td />
            </tr>
            <tr>
              <td>Outbound buffer</td>
              <td className="sk:mx-5!">
                <RenderMutedDays days={Math.abs(FC?.outboundBuffer)} />
              </td>
              <td />
            </tr>
            <tr>
              <td>Min. Desired Stock of {FC?.providerName}</td>
              <td className="sk:mx-5!">
                <RenderMutedDays days={Math.abs(FC?.inventoryBuffer)} />
              </td>
              <td />
            </tr>
            <tr>
              <td className="total">
                <PinkDarkText className="sk:m-0! sk:p-0!">
                  Committed Quantity
                </PinkDarkText>
              </td>
              <td className="total">
                <PinkDarkText className="sk:m-0! sk:p-0!">
                  <RenderMutedDays
                    renderMuted={false}
                    days={Math.abs(
                      FC?.inventoryBuffer +
                        FC?.outboundBuffer +
                        FC?.inboundBuffer +
                        FC?.transitTime,
                    )}
                  />
                </PinkDarkText>
              </td>
              <td />
            </tr>
          </table>
          <div className="sk:mt-3.5! sk:flex!">
            <span>{FC?.providerName} Inventory and Shipments</span>
            <span>=</span>
            <span>
              <BlueDarkText className="sk:m-0! sk:p-0!">
                Fulfillment Center
              </BlueDarkText>
            </span>
            {FC?.shipmentDetails?.length > 0 && (
              <>
                <span>+</span>
                <span>
                  <SuccessDarkText className="sk:m-0! sk:p-0!">
                    Shipment Total
                  </SuccessDarkText>
                </span>
              </>
            )}
            <span>-</span>
            <span>
              <PinkDarkText className="sk:m-0! sk:p-0!">
                Committed Quantity
              </PinkDarkText>
            </span>
          </div>
          <div>
            <span>
              <RenderMutedDays
                days={CommonUtility.numberWithCommas(
                  CommonUtility.roundNumber(
                    parseNumber(FC?.creditUsedFromProvider),
                    1,
                  ),
                )}
              />
            </span>
            <span>=</span>
            <span className="power">
              Zero Minimum
              <span className="to-power">1</span>
            </span>
            <span>(</span>
            <span>
              <BlueDarkText className="sk:m-0! sk:p-0!">
                <RenderMutedDays
                  renderMuted={false}
                  days={CommonUtility.roundNumber(
                    parseNumber(FC?.creditInventoryProvider),
                    1,
                  )}
                />
              </BlueDarkText>
            </span>
            {FC?.shipmentDetails?.length > 0 && (
              <>
                <span>+</span>
                <span>
                  <SuccessDarkText className="sk:m-0! sk:p-0!">
                    <RenderMutedDays
                      renderMuted={false}
                      days={CommonUtility.roundNumber(
                        parseNumber(FC?.creditInventoryShipment),
                        1,
                      )}
                    />
                  </SuccessDarkText>
                </span>
              </>
            )}
            <span>-</span>
            <span>
              <PinkDarkText className="sk:m-0! sk:p-0!">
                <RenderMutedDays
                  renderMuted={false}
                  days={Math.abs(
                    FC?.inventoryBuffer +
                      FC?.outboundBuffer +
                      FC?.inboundBuffer +
                      FC?.transitTime,
                  )}
                />
              </PinkDarkText>
            </span>
            <span>) </span>
          </div>
        </>
      ))}
      <div className="divider sk:mt-5!" />
      <div className="sk:mt-3.5! sk:flex!">
        <span>FC Inventory and Shipments</span>
        <span>=</span>
        <ProviderNames>
          {predictedOrderBuffer?.FPCreditDetails?.map((FC, i) => (
            <>
              {i !== 0 && <span>+</span>}
              {i !== 0 && <br />}
              <span>{FC.providerName} Inventory and Shipments</span>
            </>
          ))}
        </ProviderNames>
      </div>
      <div>
        <span>
          <RenderMutedDays
            days={CommonUtility.numberWithCommas(other_fp_credit)}
          />
        </span>
        <span>=</span>
        {predictedOrderBuffer?.FPCreditDetails?.map((FC, i) => {
          const val = CommonUtility.roundNumber(
            parseNumber(FC?.creditUsedFromProvider),
            1,
          );
          const show = i !== 0 ? Math.abs(val) : val;
          return (
            <React.Fragment key={i}>
              {i !== 0 && <span>{val < 0 ? '-' : '+'}</span>}
              <span>
                <RenderMutedDays days={CommonUtility.numberWithCommas(show)} />
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <div className="divider sk:mt-5! sk:mb-3.5!" />
      <div>
        <MuteText className="mute-span">Appendix</MuteText>
      </div>
      <table className="appendix">
        <tr>
          <td>1. Zero Minimum:</td>
          <td className="description">{tooltTipsText.minZero}</td>
        </tr>
      </table>
      {(other_fp_credit < 0 || other_fp_credit === 0) && (
        <div className="sk:flex! sk:items-center! sk:mt-2!">
          <MuteText className="mute-span">
            {tooltTipsText.fpCreditDetails}
          </MuteText>
        </div>
      )}
    </StyledPopup>
  );
}

function Row({ className, ...props }: DivProps) {
  return <div className={cn('sk:flex sk:gap-x-[5px]', className)} {...props} />;
}

const ICON = 'sk:-mb-[3px]';

function FCRow({ item }) {
  const hasShipments = CommonUtility.isValidArray(item.shipmentDetails);

  return (
    <>
      <AppTableRow>
        <AppTableCell colSpan={3} />
        {hasShipments ? (
          <AppTableCell verticalAlign="bottom">
            <div className="sk:flex sk:flex-wrap sk:-mx-4">
              <div className="sk:w-6/12 sk:flex-none sk:px-4">
                <MuteText>Shipment Name</MuteText>
              </div>
              <div className="sk:w-3/12 sk:flex-none sk:px-4">
                <MuteText>Qty</MuteText>
              </div>
              <div className="sk:w-3/12 sk:flex-none sk:px-4">
                <MuteText>Qty In Days</MuteText>
              </div>
            </div>
            {item.shipmentDetails.map(shipment => (
              <div className="sk:flex sk:flex-wrap sk:-mx-4">
                <div className="sk:w-6/12 sk:flex-none sk:px-4">
                  {shipment.shipmentDisplayId}
                </div>
                <Row className="sk:w-3/12 sk:flex-none sk:px-4">
                  {shipment.inventoryShipment} <MuteText>Units</MuteText>
                </Row>
                <Row className="sk:w-3/12 sk:flex-none sk:px-4">
                  {CommonUtility.roundNumber(
                    shipment.creditInventoryShipment,
                    1,
                  )}
                  <MuteText>Days</MuteText>
                </Row>
              </div>
            ))}
          </AppTableCell>
        ) : (
          <AppTableCell />
        )}
        <AppTableCell verticalAlign="bottom">
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <div className="sk:w-6/12 sk:flex-none sk:px-4">
              <MuteText>Lead Time</MuteText>
            </div>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(item.transitTime)}
              <MuteText>Days</MuteText>
            </Row>
          </div>
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <div className="sk:w-6/12 sk:flex-none sk:px-4">
              <MuteText>Inbound Buffer</MuteText>
            </div>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(item.inboundBuffer)}
              <MuteText>Days</MuteText>
            </Row>
          </div>
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <div className="sk:w-6/12 sk:flex-none sk:px-4">
              <MuteText>Outbound Buffer</MuteText>
            </div>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(item.outboundBuffer)}
              <MuteText>Days</MuteText>
            </Row>
          </div>
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <div className="sk:w-6/12 sk:flex-none sk:px-4">
              <MuteText>Min. Desired Stock</MuteText>
            </div>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(item.inventoryBuffer)}
              <MuteText>Days</MuteText>
            </Row>
          </div>
        </AppTableCell>
      </AppTableRow>
      <AppTableRow>
        <AppTableCell>{item.providerName}</AppTableCell>
        <AppTableCell />
        <AppTableCell>
          <Row>
            {item.creditInventoryProvider} <MuteText>Days</MuteText>
          </Row>
        </AppTableCell>
        <AppTableCell>
          {hasShipments ? (
            <div className="sk:flex sk:flex-wrap sk:-mx-4">
              <span className="sk:w-6/12 sk:flex-none sk:px-4">
                Shipment Total
              </span>
              <Row className="sk:w-3/12 sk:flex-none sk:px-4">
                {item.inventoryShipment} <MuteText>Units</MuteText>
              </Row>
              <Row className="sk:w-3/12 sk:flex-none sk:px-4">
                {CommonUtility.roundNumber(item?.creditInventoryShipment, 1)}{' '}
                <MuteText>Days</MuteText>
              </Row>
            </div>
          ) : (
            <MuteText>No Shipments</MuteText>
          )}
        </AppTableCell>
        <AppTableCell>
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <span className="sk:w-6/12 sk:flex-none sk:px-4">
              Committed Quantity
            </span>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(
                item?.inventoryBuffer +
                  item?.outboundBuffer +
                  item?.inboundBuffer +
                  item?.transitTime,
              )}
              <MuteText>Days</MuteText>
            </Row>
          </div>
        </AppTableCell>
      </AppTableRow>
      <AppTableRow>
        <AppTableCell colSpan={2}>
          <Row className="sk:items-center!">
            <span>FC Inventory and Shipments</span>
            <MuteText>
              <Equals className={ICON} />
            </MuteText>
            <span className="sk:whitespace-nowrap">Zero Minimum (</span>
          </Row>
        </AppTableCell>
        <AppTableCell>
          <Row>
            {item.creditInventoryProvider} <MuteText>Days</MuteText>
          </Row>
        </AppTableCell>
        <AppTableCell>
          {hasShipments && (
            <div className="sk:flex sk:flex-wrap sk:-mx-4">
              <MuteText className="sk:w-6/12 sk:flex-none sk:px-4">
                <Plus className={ICON} />{' '}
              </MuteText>
              <div className="sk:w-3/12 sk:flex-none sk:px-4" />
              <Row className="sk:w-3/12 sk:flex-none sk:px-4">
                {CommonUtility.roundNumber(item?.creditInventoryShipment, 1)}{' '}
                <MuteText>Days</MuteText>
              </Row>
            </div>
          )}
        </AppTableCell>
        <AppTableCell>
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <MuteText className="sk:w-6/12 sk:flex-none sk:px-4">
              <Minus className={ICON} />
            </MuteText>
            <Row className="sk:w-6/12 sk:flex-none sk:px-4">
              {Math.abs(
                item?.inventoryBuffer +
                  item?.outboundBuffer +
                  item?.inboundBuffer +
                  item?.transitTime,
              )}
              <MuteText>Days</MuteText>)
            </Row>
          </div>
        </AppTableCell>
      </AppTableRow>
    </>
  );
}

export function FCTable({ predictedOrderBuffer }) {
  const FCColumns = [
    {
      text: 'Provider Name',
      key: 'providerName',
      width: 2,
      textAlign: 'left',
    },
    {
      text: '',
      key: 'zero-min',
      width: 1,
      textAlign: 'left',
    },
    {
      text: 'Qty In Days',
      key: 'quantityInDays',
      textAlign: 'left',
      width: 2,
    },
    {
      text: 'Shipments',
      key: 'shipments',
      textAlign: 'left',
      width: 4,
    },
    {
      text: 'Variables',
      key: 'variables',
      textAlign: 'left',
      width: 4,
    },
  ];
  return (
    <AppTable
      wrapperClass="sk:w-full"
      noShadow
      // @ts-expect-error - TS2322: Type '{ text: string; key: string; width: number; textAlign: string; }[]' is not...
      columns={FCColumns}
      data={predictedOrderBuffer?.FPCreditDetails}
      // @ts-expect-error - TS2741: Property 'item' is missing in type '{}' but required in type '{ item: any; }'.
      RowComponent={<FCRow />}
    />
  );
}

function ContainerFormula({ className, ...props }: DivProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:text-[11px] sk:[&_span]:whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}

const SpacedRow = Row;

function SummaryTooltipContainer({
  small = false,
  children,
}: {
  small?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={small ? 'sk:w-[280px]' : 'sk:w-[500px]'}>{children}</div>
  );
}

// Sits on DarkPopup's fixed dark bubble, so a white tint rather than a token.
function MuteBorder({ className }: { className?: string }) {
  return <div className={cn('sk:h-px sk:w-full sk:bg-white/20', className)} />;
}

function ShipmentToolTip({ provider, velocityProvider }) {
  const hasShipments = CommonUtility.isValidArray(provider.shipmentDetails);
  if (!hasShipments) {
    return <div>No Shipments</div>;
  }

  return (
    <SummaryTooltipContainer>
      <BoldText>{provider?.providerName}</BoldText>
      <Row className="sk:mt-2!">
        <span>Primary Warehouse Velocity</span>
        <MuteText>=</MuteText>
        <span>{velocityProvider}</span>
        <MuteText>Units / Day</MuteText>
      </Row>
      <Row>
        <span>Qty in Days</span>
        <MuteText>=</MuteText>
        <span>Quantity In Units</span>
        <MuteText>/</MuteText>
        <span>Velocity</span>
      </Row>
      <div className="sk:flex sk:flex-wrap sk:-mx-4 sk:mt-2!">
        <MuteText className="sk:w-7/12 sk:flex-none sk:px-4">
          Shipment Name
        </MuteText>
        <MuteText className="sk:w-2/12 sk:flex-none sk:px-4">Qty</MuteText>
        <MuteText className="sk:w-3/12 sk:flex-none sk:px-4">
          Qty in Days
        </MuteText>
      </div>
      {provider.shipmentDetails.map(shipment => (
        <div className="sk:flex sk:flex-wrap sk:-mx-4">
          <div className="sk:w-7/12 sk:flex-none sk:px-4">
            {shipment.shipmentDisplayId}
          </div>
          <Row className="sk:w-2/12 sk:flex-none sk:px-4">
            {shipment.inventoryShipment} <MuteText>Units</MuteText>
          </Row>
          <Row className="sk:w-3/12 sk:flex-none sk:px-4">
            {CommonUtility.roundNumber(shipment.creditInventoryShipment, 2)}
            <MuteText>Days</MuteText>
          </Row>
        </div>
      ))}
      <MuteBorder className="sk:my-1" />
      <div className="sk:flex sk:flex-wrap sk:-mx-4">
        <div className="sk:w-5/12 sk:flex-none sk:px-4" />
        <Row className="sk:w-4/12 sk:flex-none sk:px-4 sk:justify-end!">
          <span>Shipment Total</span>
        </Row>
        <Row className="sk:w-3/12 sk:flex-none sk:px-4">
          <span>
            {CommonUtility.roundNumber(provider.creditInventoryShipment)}
          </span>
          <MuteText>Days</MuteText>
        </Row>
      </div>
    </SummaryTooltipContainer>
  );
}

function InventoryToolTip({ provider, velocityProvider }) {
  return (
    <SummaryTooltipContainer>
      <BoldText>{provider?.providerName}</BoldText>
      <Row className="sk:mt-2!">
        <span>Primary Warehouse Velocity</span>
        <MuteText>=</MuteText>
        <span>{velocityProvider}</span>
        <MuteText>Units / Day</MuteText>
      </Row>
      <Row>
        <span>Fulfillment Center Quantity</span>
        <MuteText>=</MuteText>
        <span>{provider?.existingInventoryProvider}</span>
        <MuteText>Units</MuteText>
      </Row>
      <Row className="sk:mt-2!">
        <span>Quantity in Days of {provider?.providerName}</span>
        <MuteText>=</MuteText>
        <span>Quantity</span>
        <MuteText>/</MuteText>
        <span>Velocity</span>
        <MuteText>=</MuteText>
        <span>
          {CommonUtility.roundNumber(provider?.creditInventoryProvider, 2)}
        </span>
        <MuteText>Days</MuteText>
      </Row>
    </SummaryTooltipContainer>
  );
}

function CommittedQuantityToolTip({ provider }) {
  const committedQty = Math.abs(
    provider?.inventoryBuffer +
      provider?.outboundBuffer +
      provider?.inboundBuffer +
      provider?.transitTime,
  );
  return (
    <SummaryTooltipContainer small>
      <BoldText>{provider?.providerName}</BoldText>
      <div className="sk:mt-2!">
        <div className="sk:flex sk:flex-wrap sk:-mx-4">
          <div className="sk:w-7/12 sk:flex-none sk:px-4">
            <span>Lead Time</span>
          </div>
          <Row className="sk:w-5/12 sk:flex-none sk:px-4">
            {Math.abs(provider.transitTime)}
            <MuteText>Days</MuteText>
          </Row>
        </div>
        <div className="sk:flex sk:flex-wrap sk:-mx-4">
          <div className="sk:w-7/12 sk:flex-none sk:px-4">
            <span>Inbound Buffer</span>
          </div>
          <Row className="sk:w-5/12 sk:flex-none sk:px-4">
            {Math.abs(provider.inboundBuffer)}
            <MuteText>Days</MuteText>
          </Row>
        </div>
        <div className="sk:flex sk:flex-wrap sk:-mx-4">
          <div className="sk:w-7/12 sk:flex-none sk:px-4">
            <span>Outbound Buffer</span>
          </div>
          <Row className="sk:w-5/12 sk:flex-none sk:px-4">
            {Math.abs(provider.outboundBuffer)}
            <MuteText>Days</MuteText>
          </Row>
        </div>
        <div className="sk:flex sk:flex-wrap sk:-mx-4">
          <div className="sk:w-7/12 sk:flex-none sk:px-4">
            <span>Min. Desired Stock</span>
          </div>
          <Row className="sk:w-5/12 sk:flex-none sk:px-4">
            {Math.abs(provider.inventoryBuffer)}
            <MuteText>Days</MuteText>
          </Row>
        </div>
      </div>
      <MuteBorder className="sk:my-1" />
      <div className="sk:flex sk:flex-wrap sk:-mx-4">
        <div className="sk:w-7/12 sk:flex-none sk:px-4">
          <span>Committed Quantity</span>
        </div>
        <Row className="sk:w-5/12 sk:flex-none sk:px-4">
          {Math.abs(committedQty)}
          <MuteText>Days</MuteText>
        </Row>
      </div>
    </SummaryTooltipContainer>
  );
}

export function FCInventorySummary({
  predictedOrderBuffer,
  velocityProvider,
  other_fp_credit,
}) {
  const hasFCs = CommonUtility.isValidArray(
    predictedOrderBuffer?.FPCreditDetails,
  );

  const hasMultipleFCs = predictedOrderBuffer?.FPCreditDetails?.length > 1;

  if (!hasFCs) return <></>;

  return (
    <>
      {hasMultipleFCs && (
        <ContainerFormula className="sk:mb-1">
          <MuteText>Inventory and Shipments</MuteText>
          <MuteText className="sk:mx-2!">=</MuteText>
          <SpacedRow className="sk:flex-wrap!">
            <MuteText>Fulfillment Center Units (Days)</MuteText>
            <MuteText>+</MuteText>
            <MuteText>Inbound Shipment Units (Days)</MuteText>
            <MuteText>-</MuteText>
            <MuteText>Committed Quantity (Days)</MuteText>
          </SpacedRow>
        </ContainerFormula>
      )}
      <ContainerFormula className="sk:mb-1">
        <BlueText>FC Inventory and Shipments</BlueText>
        <span className="sk:mx-2!">=</span>
        <SpacedRow className="sk:flex-wrap!">
          {predictedOrderBuffer?.FPCreditDetails?.map((FC, i) => (
            <Fragment key={FC.providerId}>
              {hasMultipleFCs ? (
                <BlueText>
                  Zero Minimum ({FC.providerName} Inventory and Shipments)
                </BlueText>
              ) : (
                <BlueText>
                  Zero Minimum (Fulfillment Center Units (Days) + Inbound
                  Shipment Units (Days) - Committed Quantity (Days))
                </BlueText>
              )}
              {predictedOrderBuffer?.FPCreditDetails.length - 1 !== i && (
                <span> + </span>
              )}
            </Fragment>
          ))}
        </SpacedRow>
      </ContainerFormula>
      <FormulaContainer className="sk:mb-2!">
        <BlueText>
          {CommonUtility.numberWithCommas(Math.abs(other_fp_credit))} Days
        </BlueText>
        <span>=</span>
        <FormulaContainer className="sk:flex-wrap!">
          {predictedOrderBuffer?.FPCreditDetails?.map((FC, i) => {
            const committedQty = Math.abs(
              FC?.inventoryBuffer +
                FC?.outboundBuffer +
                FC?.inboundBuffer +
                FC?.transitTime,
            );
            return (
              <Fragment key={FC.providerId}>
                {i !== 0 && <span>+</span>}
                <SpacedRow>
                  <BlueText>Zero Minimum</BlueText>
                  <span>(</span>
                  <SimpleDarkToolTip
                    trigger={
                      <BlueText>
                        {CommonUtility.roundNumber(FC.creditInventoryProvider)}{' '}
                        Days
                        <Info size={11} className="sk:ml-1!" />
                      </BlueText>
                    }
                    helpText={
                      <InventoryToolTip
                        velocityProvider={velocityProvider}
                        provider={FC}
                      />
                    }
                    helpPosition="top center"
                    hoverable
                  />
                  <span>+</span>
                  <SimpleDarkToolTip
                    trigger={
                      <BlueText>
                        {CommonUtility.roundNumber(FC.creditInventoryShipment)}{' '}
                        Days <Info size={11} className="sk:ml-1!" />
                      </BlueText>
                    }
                    helpText={
                      <ShipmentToolTip
                        velocityProvider={velocityProvider}
                        provider={FC}
                      />
                    }
                    helpPosition="top center"
                    hoverable
                  />
                  <span>-</span>
                  <SimpleDarkToolTip
                    trigger={
                      <BlueText>
                        {CommonUtility.roundNumber(committedQty)} Days
                        <Info size={11} className="sk:ml-1!" />
                      </BlueText>
                    }
                    helpText={
                      <CommittedQuantityToolTip
                        // @ts-expect-error - TS2322: Type '{ velocityProvider: any; provider: any; }' is not assignable to type 'Intr...
                        velocityProvider={velocityProvider}
                        provider={FC}
                      />
                    }
                    helpPosition="top center"
                    hoverable
                  />
                  <span>)</span>
                </SpacedRow>
              </Fragment>
            );
          })}
        </FormulaContainer>
      </FormulaContainer>
    </>
  );
}
